import jsPDF from 'jspdf';
import logo from '../assets/logo_documentos.png.png';
import {
  NAVY, GRAY, BLACK, LGRAY,
  v, fmt, f,
  ul, field,
} from './pdfHelpers';

export interface SingleEyeExamPDFParams {
  /** Título do exame, ex: "Acuidade Visual", "Refração", "Tonometria" */
  examTitle: string;
  /** Rótulos das colunas, ex: ["SC","CC","P/H"] ou ["VALOR"] */
  fieldLabels: string[];
  odValues: (string | number | null | undefined)[];
  oeValues: (string | number | null | undefined)[];
  /** Prefixo do nome do arquivo salvo */
  fileSlug: string;
  patientName?: string | null;
  patientCPF?: string | null;
  doctorName?: string | null;
  doctorCRM?: string | null;
  /** ISO date; default: hoje */
  date?: string | null;
}

const PAGE_H = 297; // A4, mm

export async function generateSingleEyeExamPDF(params: SingleEyeExamPDFParams): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const LX = 15;  // margem esquerda
  const W  = 180; // largura útil

  // ── CABEÇALHO ────────────────────────────────────────────────────────────────
  let y = 10;
  const logoH = 24;
  const logoW = logoH * (643/388);
  doc.addImage(logo, 'PNG', (210-logoW)/2, y, logoW, logoH);
  y += 30;

  ul(doc, LX, y, W); y += 8;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(15);
  doc.text(params.examTitle.toUpperCase(), 105, y, { align:'center' });

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(8.5);
  doc.text('DATA:', LX+W-38, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(fmt(params.date ?? new Date().toISOString()), LX+W-25, y);

  y += 4; ul(doc, LX, y, W); y += 14;

  // ── PACIENTE ───────────────────────────────────────────────────────────────────
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('Paciente:', LX, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','bold'); doc.setFontSize(10.5);
  doc.text(v(params.patientName) || '—', LX+22, y);

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('CPF:', LX+W-45, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(v(params.patientCPF) || '—', LX+W-33, y);

  y += 4; ul(doc, LX, y, W);
  y += 22;

  // ── TABELA OD/OE ─────────────────────────────────────────────────────────────────
  const nCols = params.fieldLabels.length;
  const labelColW = 24;
  const colW = (W - labelColW) / nCols;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(10);
  params.fieldLabels.forEach((label, i) => {
    doc.text(label, LX+labelColW+colW*i+colW/2, y, { align:'center' });
  });
  y += 4;
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.3);
  doc.line(LX, y, LX+W, y);
  y += 14;

  const drawRow = (eyeLabel: string, values: (string|number|null|undefined)[]) => {
    f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(11);
    doc.text(eyeLabel, LX, y);
    values.forEach((val, i) => {
      const cx = LX+labelColW+colW*i+colW/2;
      const hasVal = val !== null && val !== undefined && String(val).trim() !== '';
      f(doc,'text', hasVal ? BLACK : LGRAY);
      doc.setFont('helvetica','normal'); doc.setFontSize(12);
      doc.text(hasVal ? String(val) : '—', cx, y, { align:'center' });
    });
    ul(doc, LX+labelColW-4, y+3, W-labelColW+4);
    y += 18;
  };

  drawRow('OD', params.odValues);
  drawRow('OE', params.oeValues);

  // ── ASSINATURA / RODAPÉ ──────────────────────────────────────────────────────────
  const sigY = Math.max(y + 20, PAGE_H - 45);
  const finalSigY = sigY > PAGE_H - 20 ? PAGE_H - 40 : sigY;

  const sigW = 90;
  const sigX = LX + (W - sigW) / 2;
  ul(doc, sigX, finalSigY, sigW);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('Assinatura e Carimbo do Médico', sigX+sigW/2, finalSigY+3, { align:'center' });
  field(doc, 'Médico:', v(params.doctorName), sigX, finalSigY+9, sigW/2-2);
  field(doc, 'CRM:', v(params.doctorCRM), sigX+sigW/2+2, finalSigY+9, sigW/2-2);

  const FY = finalSigY + 20;
  ul(doc, LX, FY, W);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6);
  doc.text(
    'Rua Exemplo, 123 - Centro - Sua Cidade/UF - CEP 00000-000     (00) 0000-0000     www.sagradavisao.com.br',
    105, FY+4, { align:'center' },
  );

  // ── SALVAR ────────────────────────────────────────────────────────────────────
  const name     = v(params.patientName ?? 'paciente');
  const datePart = fmt(params.date ?? new Date().toISOString()).replace(/\//g,'-');
  doc.save(`${params.fileSlug}_${name.replace(/\s+/g,'_').toLowerCase()}_${datePart}.pdf`);
}
